export interface HudStats {
  /** renderer.info.render for the whole frame (all passes). */
  render?: { calls: number; triangles: number };
  frameMs: number;
  /** GPU time per frame (timer query), null when unavailable. */
  gpuMs?: number | null;
  fps: number;
  activeTiles: number;
  queueDepth: number;
  inFlight: number;
  applyQueue: number;
  extra: string;
}

export type Quality = 'low' | 'mid' | 'high';

export interface GraphicsSettings {
  quality: Quality;
  shadows: boolean;
  ao: boolean;
}

export interface HudCallbacks {
  onRegenerate: (seedText: string) => void;
  onToggleOverlay: (on: boolean) => void;
  onGraphics: (g: GraphicsSettings) => void;
  onAudio: (volume: number, muted: boolean) => void;
  audioState: () => { volume: number; muted: boolean; running: boolean };
}

export const HELP = {
  fly: 'Click to look · WASD move · E/Space up · Q/C down<br/>Shift fast · wheel/+/- speed · Esc release',
  walk: 'Click to look · WASD walk · Shift run · Space jump<br/>F get into your car · Esc release',
  drive: 'W/S throttle, brake, reverse · A/D steer · Space handbrake<br/>F get out · R reset car · mouse orbits the camera · Esc release',
};

/** On-screen readout plus seed input, regenerate button and overlay toggle. */
export class Hud {
  private readonly stats: HTMLDivElement;
  private readonly seedInput: HTMLInputElement;
  private readonly overlayCheck: HTMLInputElement;
  private readonly help: HTMLDivElement;
  private readonly soundNote: HTMLSpanElement;
  private readonly audioState: HudCallbacks['audioState'];

  private readonly root: HTMLDivElement;
  private readonly graphicsControls: { q: HTMLSelectElement; sh: HTMLInputElement; ao: HTMLInputElement };
  private readonly soundControls: { on: HTMLInputElement; vol: HTMLInputElement };
  private readonly seedInputRef: () => HTMLInputElement;

  constructor(parent: HTMLElement, seedText: string, cb: HudCallbacks, graphics: GraphicsSettings, shown = false) {
    const root = document.createElement('div');
    root.className = shown ? 'hud' : 'hud hidden';
    this.root = root;
    this.stats = document.createElement('div');
    root.appendChild(this.stats);

    const row = document.createElement('div');
    row.className = 'row';
    this.seedInput = document.createElement('input');
    this.seedInput.type = 'text';
    this.seedInput.value = seedText;
    this.seedInput.title = 'World seed (integer or any text)';
    const regen = document.createElement('button');
    regen.textContent = 'Regenerate';
    regen.onclick = () => cb.onRegenerate(this.seedInput.value);
    this.seedInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') cb.onRegenerate(this.seedInput.value);
      e.stopPropagation();
    });
    const lbl = document.createElement('label');
    this.overlayCheck = document.createElement('input');
    this.overlayCheck.type = 'checkbox';
    this.overlayCheck.onchange = () => cb.onToggleOverlay(this.overlayCheck.checked);
    lbl.append(this.overlayCheck, 'Overlay (Tab)');
    row.append('Seed', this.seedInput, regen, lbl);
    root.appendChild(row);

    // Graphics: a preset sets shadows/AO/resolution; the checkboxes override it.
    const gfx = document.createElement('div');
    gfx.className = 'row';
    const q = document.createElement('select');
    for (const v of ['low', 'mid', 'high']) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = v;
      q.appendChild(o);
    }
    q.value = graphics.quality;
    const check = (label: string, on: boolean) => {
      const l = document.createElement('label');
      const c = document.createElement('input');
      c.type = 'checkbox';
      c.checked = on;
      l.append(c, label);
      return [l, c] as const;
    };
    const [shL, sh] = check('Shadows', graphics.shadows);
    const [aoL, ao] = check('AO', graphics.ao);
    const emit = () => cb.onGraphics({ quality: q.value as Quality, shadows: sh.checked, ao: ao.checked });
    q.onchange = () => {
      sh.checked = q.value !== 'low';
      ao.checked = q.value !== 'low';
      emit();
    };
    sh.onchange = emit;
    ao.onchange = emit;
    gfx.append('Quality', q, shL, aoL);
    root.appendChild(gfx);
    this.graphicsControls = { q, sh, ao };

    // Sound: on/off (M) and volume; the browser only starts audio after a click or key press.
    this.audioState = cb.audioState;
    const snd = document.createElement('div');
    snd.className = 'row';
    const a0 = cb.audioState();
    const [onL, on] = check('Sound (M)', !a0.muted);
    const vol = document.createElement('input');
    vol.type = 'range';
    vol.min = '0';
    vol.max = '1';
    vol.step = '0.01';
    vol.value = String(a0.volume);
    vol.title = 'Volume';
    vol.className = 'volume';
    this.soundNote = document.createElement('span');
    this.soundNote.className = 'note';
    const emitAudio = () => cb.onAudio(Number(vol.value), !on.checked);
    on.onchange = emitAudio;
    vol.oninput = () => {
      if (!on.checked && Number(vol.value) > 0) on.checked = true;
      emitAudio();
    };
    // Keep arrow keys and Space for driving, not the slider.
    vol.addEventListener('keydown', (e) => e.preventDefault());
    snd.append(onL, vol, this.soundNote);
    root.appendChild(snd);
    this.soundControls = { on, vol };
    this.seedInputRef = () => this.seedInput;
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (e.code !== 'KeyM' || (t && (t.tagName === 'INPUT' && (t as HTMLInputElement).type === 'text'))) return;
      on.checked = !on.checked;
      emitAudio();
    });

    this.help = document.createElement('div');
    this.help.className = 'help';
    this.setHelp(HELP.fly);
    root.appendChild(this.help);
    parent.appendChild(root);

    window.addEventListener('keydown', (e) => {
      if (e.code === 'F3' || e.code === 'Backquote') {
        e.preventDefault();
        this.root.classList.toggle('hidden');
      }
      if (e.code === 'Tab') {
        e.preventDefault();
        this.overlayCheck.checked = !this.overlayCheck.checked;
        cb.onToggleOverlay(this.overlayCheck.checked);
      }
    });
  }

  /** Show settings changed elsewhere (the menu). */
  setGraphics(g: GraphicsSettings): void {
    this.graphicsControls.q.value = g.quality;
    this.graphicsControls.sh.checked = g.shadows;
    this.graphicsControls.ao.checked = g.ao;
  }

  setSeed(text: string): void {
    this.seedInputRef().value = text;
  }

  /** Controls text for the current navigation mode. */
  setHelp(text: string): void {
    this.help.innerHTML = text + '<br/>1 fly · 2 walk · 3 drive · Tab overlay · in overlay: drag pan, wheel zoom, click a room';
  }

  update(s: HudStats): void {
    if (this.root.classList.contains('hidden')) return;
    const a = this.audioState();
    const note = a.running ? '' : 'click to start';
    if (this.soundNote.textContent !== note) this.soundNote.textContent = note;
    const sc = this.soundControls;
    if (document.activeElement !== sc.vol) sc.vol.value = String(a.volume);
    sc.on.checked = !a.muted;
    this.stats.innerHTML =
      `frame ${s.frameMs.toFixed(1)} ms (${s.fps.toFixed(0)} fps)` +
      (s.gpuMs != null ? ` · gpu ${s.gpuMs.toFixed(1)} ms` : '') +
      (s.render ? ` · ${s.render.calls} draws · ${(s.render.triangles / 1e6).toFixed(2)} M tris` : '') +
      `<br/>` +
      `active tiles ${s.activeTiles}<br/>` +
      `worker queue ${s.queueDepth} · running ${s.inFlight} · to apply ${s.applyQueue}` +
      (s.extra ? `<br/>${s.extra}` : '');
  }
}
